/**
 * API client service
 * Handles all HTTP requests to the backend API
 * Uses axios with interceptors for error handling and authentication
 */

import axios from 'axios'
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from '@/types'

/**
 * Create and configure axios instance
 * In development, Vite proxy handles the routing to backend
 * In production, VITE_API_URL should be set to the actual backend URL
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for session management
})

/**
 * Request interceptor
 * Gets fresh Firebase token before each request to ensure it's never expired
 * Also attaches invitation code if present (for new user registration)
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Get fresh token from Firebase (handles refresh automatically if needed)
    const { getCurrentUser } = await import('@/services/auth')
    const user = getCurrentUser()

    if (user && config.headers) {
      try {
        // Firebase automatically refreshes if token is expired
        // This means sessions can last as long as the refresh token is valid (days/weeks)
        const token = await user.getIdToken(false) // false = only refresh if expired
        config.headers.Authorization = `Bearer ${token}`

        // Check if there's an invitation code stored (for new user registration)
        // The backend will check for this on first authenticated request
        const invitationCode = sessionStorage.getItem('invitation_code')
        if (invitationCode) {
          // Add invitation code to request body for POST/PUT/PATCH
          if (config.method && ['post', 'put', 'patch'].includes(config.method.toLowerCase())) {
            config.data = { ...config.data, invitationCode }
          }
          // Also add as query param for GET/DELETE requests
          if (!config.params) {
            config.params = {}
          }
          config.params.invitationCode = invitationCode
        }
      } catch (error) {
        console.error('Failed to get token for request:', error)
        // Token get failed, will result in 401 from backend
      }
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

/**
 * Response interceptor
 * Handles errors and formats error responses
 * Also clears invitation code after successful user creation
 */
apiClient.interceptors.response.use(
  (response) => {
    // Clear invitation code after successful response (user was created successfully)
    const invitationCode = sessionStorage.getItem('invitation_code')
    if (invitationCode) {
      // Only clear if this was an authenticated request (had Authorization header)
      const authHeader = response.config.headers?.Authorization
      if (authHeader && authHeader.startsWith('Bearer ')) {
        sessionStorage.removeItem('invitation_code')
      }
    }
    return response
  },
  (error: AxiosError<ApiError>) => {
    // Handle network errors
    if (!error.response) {
      return Promise.reject({
        message: 'Network error - please check your connection',
        statusCode: 0,
      } as ApiError)
    }

    // Format API error response
    const apiError: ApiError = {
      message: error.response.data?.message || 'An unexpected error occurred',
      statusCode: error.response.status,
      errors: error.response.data?.errors,
    }

    // Handle 401 Unauthorized - redirect to login
    if (error.response.status === 401) {
      localStorage.removeItem('auth_token')
      // Will implement router redirect when auth is ready
      // router.push('/login')
    }

    // Handle 403 Forbidden - might be invitation code required
    if (error.response.status === 403) {
      // If message contains "invitation code", it's a new user registration that needs a code
      if (apiError.message?.toLowerCase().includes('invitation code')) {
        // This error will be caught by the login page to show invitation code input
        apiError.requiresInvitationCode = true
      }
    }

    return Promise.reject(apiError)
  }
)

/**
 * Health check endpoint
 * Tests backend connectivity
 */
export async function checkHealth() {
  const response = await apiClient.get('/health')
  return response.data
}

export default apiClient